import {
  CUSTOM_INDEX_NAME_REGEX,
  EMAIL_ADDRESS_REGEX,
  TEAM_MEMBER_NAME_REGEX,
} from '../../../constants/constants';
import {UserRoleIds} from '../../../types/user-management/userManagementTypes';
import {CustomIndexAnalyzersFormInputKey} from '../../indexing/model';
import {t} from 'i18next';
import {
  TEAM_MEMBER_NAME_MAX_LENGTH,
  TeamMemberInfoFormInputKey,
  AddClientInfoInputKey,
  EMAIL_MAX_LENGTH,
  CustomIndexInfoFormInputKey,
  CUSTOM_INDEX_NAME_MAX_LENGTH,
} from './models';
import * as yup from 'yup';

const ClientExtractInfoValidationSchema = () =>
  yup.object().shape({
    [AddClientInfoInputKey.client]: yup
      .string()
      .required(t('validation.clientRequired')),
  });

const editClientInfoValidationSchema = () =>
  yup.object().shape({
    [AddClientInfoInputKey.role]: yup
      .string()
      .required(t('validation.roleRequired')),
    [AddClientInfoInputKey.permissionGroup]: yup
      .string()
      .when(AddClientInfoInputKey.role, (role, schema) => {
        if (role[0]?.toLowerCase() == UserRoleIds.ClientAdmin.toLowerCase()) {
          return schema.optional();
        } else {
          return schema.required(t('validation.permissionGroupRequired'));
        }
      }),
    [AddClientInfoInputKey.projects]: yup.string().optional(),
    [AddClientInfoInputKey.projPermissionGroup]: yup
      .string()
      .when(
        [AddClientInfoInputKey.role, AddClientInfoInputKey.projects],
        ([role, project], schema) => {
          if (role?.toLowerCase() === UserRoleIds.ClientAdmin.toLowerCase()) {
            return schema.optional();
          }
          if (project) {
            return schema.required(t('validation.projPermissionGroupRequired'));
          }
          return schema.optional();
        }
      ),
    [AddClientInfoInputKey.workspaces]: yup.array().optional(),
  });

export const addClientInfoValidationSchema = () =>
  editClientInfoValidationSchema().concat(ClientExtractInfoValidationSchema());

const editTeamMemberInformationFormValidationSchema = () =>
  yup.object().shape({
    [TeamMemberInfoFormInputKey.firstName]: yup
      .string()
      .required(t('validation.firstNameRequired'))
      .max(TEAM_MEMBER_NAME_MAX_LENGTH, t('validation.maxLength50'))
      .matches(TEAM_MEMBER_NAME_REGEX, t('validation.invalidNameSpecialChars')),
    [TeamMemberInfoFormInputKey.lastName]: yup
      .string()
      .required(t('validation.lastNameRequired'))
      .max(TEAM_MEMBER_NAME_MAX_LENGTH, t('validation.maxLength50'))
      .matches(TEAM_MEMBER_NAME_REGEX, t('validation.invalidNameSpecialChars')),
    [TeamMemberInfoFormInputKey.email]: yup
      .string()
      .required(t('validation.emailRequired'))
      .max(EMAIL_MAX_LENGTH, t('validation.maxLength150'))
      .matches(EMAIL_ADDRESS_REGEX, t('validation.enterValidEmailAddress')),
    [TeamMemberInfoFormInputKey.isBillable]: yup.boolean().required(),
    [TeamMemberInfoFormInputKey.isLimitIp]: yup.boolean().required(),
  });

const teamMemberExtraInformationFormValidationSchema = () =>
  yup.object().shape({
    [TeamMemberInfoFormInputKey.isActive]: yup.boolean().required(),
    [TeamMemberInfoFormInputKey.ipAddressList]: yup.array().required(),
  });

const editTeamMemberExtraInformationFormValidationSchema = () =>
  yup.object().shape({
    [TeamMemberInfoFormInputKey.ipAddressList]: yup
      .array()
      .of(yup.string().required())
      .test(
        'ip-list-required',
        t('validation.atLeastOneIpAddressRequired'),
        function () {
          return true;
        }
      ),
  });

export const teamMemberInformationFormValidationSchema = () =>
  editTeamMemberInformationFormValidationSchema().concat(
    teamMemberExtraInformationFormValidationSchema()
  );

export const editTeamMemberFormValidationSchema = () =>
  editTeamMemberInformationFormValidationSchema().concat(
    editTeamMemberExtraInformationFormValidationSchema()
  );

export const bulkEditClientInfoValidationSchema = () =>
  yup.object().shape({
    [AddClientInfoInputKey.role]: yup
      .string()
      .required(t('validation.roleRequired')),
    [AddClientInfoInputKey.projects]: yup.string(),
    [AddClientInfoInputKey.projectPermissionGroup]: yup.string(),
    [AddClientInfoInputKey.clientPermissionGroup]: yup
      .string()
      .required(t('validation.permissionGroupRequired')),
    [AddClientInfoInputKey.workspace]: yup.string(),
  });

export const customIndexFormValidationSchema = () =>
  yup.object().shape({
    [CustomIndexInfoFormInputKey.customIndex]: yup
      .string()
      .required(t('validation.customIndexRequired'))
      .max(CUSTOM_INDEX_NAME_MAX_LENGTH, t('validation.maxLength50'))
      .matches(CUSTOM_INDEX_NAME_REGEX, t('validation.invalidCharsInName')),
  });
export const customIndexAnalyzerFormValidationSchema = () =>
  yup.object().shape({
    [CustomIndexAnalyzersFormInputKey.customName]: yup
      .string()
      .required(t('validation.customNameRequired'))
      .max(CUSTOM_INDEX_NAME_MAX_LENGTH, t('validation.maxLength50'))
      .matches(
        CUSTOM_INDEX_NAME_REGEX,
        t('validation.nameContainsInvalidChars')
      ),
    [CustomIndexAnalyzersFormInputKey.stopWords]: yup
      .array()
      .of(yup.string().trim())
      .nullable()
      .default([]),
    [CustomIndexAnalyzersFormInputKey.caseConversionFlag]: yup
      .boolean()
      .nullable()
      .default(false),
    [CustomIndexAnalyzersFormInputKey.whitespaceCharList]: yup
      .string()
      .nullable()
      .default(''),
  });
